import { Wallet, AlertTriangle, Inbox, PiggyBank, Clock } from 'lucide-react';
import type { ReconStats } from '../../types/paymentRecon';
import { formatMoney } from '../../utils/paymentReconFormat';

interface Props {
    stats: ReconStats | null;
    onUnresolvedClick?: () => void;
}

export function KpiTiles({ stats, onUnresolvedClick }: Props) {
    const tiles = [
        {
            label: 'Open receivables',
            value: formatMoney(stats?.totalOpen ?? 0),
            sub: `${stats?.openCount ?? 0} invoices`,
            icon: Wallet,
            tone: 'text-slate-700',
        },
        {
            label: 'Overdue',
            value: formatMoney(stats?.overdueAmount ?? 0),
            sub: `${stats?.overdueCount ?? 0} invoices`,
            icon: AlertTriangle,
            tone: 'text-rose-700',
        },
        {
            label: 'Unresolved payments',
            value: formatMoney(stats?.unresolvedAmount ?? 0),
            sub: `${stats?.unresolvedCount ?? 0} to review`,
            icon: Inbox,
            tone: 'text-amber-700',
            onClick: onUnresolvedClick,
        },
        {
            label: 'On account',
            value: formatMoney(stats?.onAccountAmount ?? 0),
            sub: `${stats?.onAccountCount ?? 0} credits`,
            icon: PiggyBank,
            tone: 'text-sky-700',
        },
        {
            label: 'DSO (est.)',
            value: stats?.dsoEstimate != null ? `${stats.dsoEstimate} d` : '–',
            sub: 'last 90 days',
            icon: Clock,
            tone: 'text-slate-700',
        },
    ];

    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {tiles.map((t) => {
                const Icon = t.icon;
                const clickable = !!t.onClick;
                return (
                    <div
                        key={t.label}
                        onClick={t.onClick}
                        className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${clickable ? 'cursor-pointer hover:border-amber-300' : ''}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{t.label}</span>
                            <Icon className={`h-4 w-4 ${t.tone}`} />
                        </div>
                        <div className={`mt-1 text-lg font-semibold ${t.tone}`}>{t.value}</div>
                        <div className="text-xs text-slate-400">{t.sub}</div>
                    </div>
                );
            })}
        </div>
    );
}
